import type { Tutorial } from '../../shared/types.js';

export const personasTutorial: Tutorial = {
  name: 'personas',
  title: 'Create a reusable agent persona',
  description:
    'Bundle an agent type, model, environment, and default prompt into a named persona you can launch on demand. No per-launch setup required.',
  category: 'personas',
  docPages: ['concepts/personas'],
  agenda: [
    'What personas are',
    'Set the name and prompts',
    'Build its environment',
    'Save and launch it any time',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'What personas are',
      body: "A persona is a saved agent identity. It packages an agent type, model, blueprint, attachments, and a default prompt so you can launch a purpose-built agent in one click instead of re-configuring it each time. Now let's create one.",
      path: '/configure/build/agents/personas',
    },
    {
      id: 'open-dialog',
      kind: 'action',
      trigger: 'click',
      title: 'Create a persona',
      body: 'Click the button to open the new-persona form.',
      target: '[data-tutorial-id="persona-new"]',
      placement: 'bottom',
    },
    {
      id: 'name',
      kind: 'action',
      trigger: 'input',
      title: 'Name your persona',
      body: 'Give the persona a clear, recognizable name and optionally pick an icon to the left. This is how it appears in the launch dialog and search. Type a name, or use **Fill example**, to continue.',
      example: 'Grafana DevOps',
      target: '#persona-name',
      placement: 'bottom',
    },
    {
      id: 'system-prompt',
      kind: 'info',
      title: 'System prompt',
      body: "Optionally add a system prompt with standing instructions for the agent. System prompts are typically used to define an agent's role, goals, and conventions, as well as any guardrails or constraints. When set, the system prompt replaces the default mode-based system instructions.",
      target: '#persona-system-prompt',
      placement: 'top',
    },
    {
      id: 'default-prompt',
      kind: 'info',
      title: 'Default prompt',
      body: 'Set a default prompt that pre-fills the launch dialog as a starting point. Prompts can be edited before each launch. Leave the prompt section empty to start fresh each time.',
      target: '#persona-default-prompt',
      placement: 'top',
    },
    {
      id: 'config-attachments',
      kind: 'info',
      title: 'Build its environment',
      body: "The **Attachments** bar extends the agent to tailor its functionality. Each attachment offers functionality that the persona's agent can use. This includes a git repository, a Tailscale profile, MCP servers, AI gateways, and more. Installed plugins define the list of available attachments. Each attachment is listed with its own option chip. Click a chip to bake it in, or leave it; none of it is required. Environment variables and secrets live under **Advanced**.",
      target: '[role="dialog"] [data-testid="agent-attachments-editor"]',
      placement: 'top',
    },
    {
      id: 'create',
      kind: 'action',
      trigger: 'click',
      title: 'Save the persona',
      body: 'Click "Create persona" to save it. It immediately becomes available to launch and to reference from automations.',
      target: '[data-tutorial-id="persona-create-submit"]',
      placement: 'top',
    },
    {
      id: 'launch',
      kind: 'info',
      title: 'Launch it any time',
      body: 'Your persona now shows up as a card on this page. Use "Launch" to start an agent pre-configured from it, "Duplicate" to make a copy, or "Edit" to tweak its defaults.',
    },
  ],
};
